import type { Ref } from 'vue'
import { shallowReadonly, shallowRef, watch } from 'vue'

import type { World } from '~/lib/three-d-resources/world'
import type { DrillLayer, DrillLabelLayer } from '~/entities/drillLabel'
import { makeEscapedRegExp } from '~/lib/utils'

export interface DrillDataSetLayerGroup {
  type: 'drill'
  dataSetId: number
  localizationId: number
  name: string
  layers: DrillLayer[]
  id: string
  allVisible: boolean
  someVisible: boolean
}

export function useDrillDataSetGroups({ world, search }: { world: World; search: Ref<string> }) {
  const layerGroups = shallowRef<DrillDataSetLayerGroup[]>([])

  watch(search, rebuildLayerGroups)

  function clearLayerGroups() {
    layerGroups.value = []
  }

  function rebuildLayerGroups() {
    const { drillDataSetManager } = world

    if (!drillDataSetManager) return

    const dataSets = drillDataSetManager.drillDataSets

    if (!dataSets) {
      layerGroups.value = []
      return
    }

    layerGroups.value = dataSets.reduce<DrillDataSetLayerGroup[]>((acc, dataSet) => {
      const aggregate = drillDataSetManager.getDrillDataSetAggregate(dataSet.data_set_id)

      if (!aggregate) return acc

      const layers = _filterHolesBySearchTerm(aggregate.drillLayers, search.value)

      if (!layers.length) return acc

      const labels = layers.flatMap(l => l.files.flatMap(f => f.labels))

      return acc.concat({
        type: 'drill',
        layers,
        dataSetId: dataSet.data_set_id,
        localizationId: dataSet.localization.id,
        name: aggregate.data_set.name,
        id: `${dataSet.data_set_id}-${dataSet.localization_id}`,
        someVisible: _areSomeGroupsVisible(layers, labels),
        allVisible: _areAllGroupsVisible(layers, labels),
      })
    }, [])
  }

  function _areAllGroupsVisible(layers: DrillLayer[], labels: DrillLabelLayer[]): boolean {
    if (!layers.some(l => l.type === 'cluster')) return labels.every(l => l.visible)

    const cLabels = layers.flatMap(x => x.files.flatMap(y => y.labels))

    return layers.every(l => l.visible) && cLabels.every(l => l.visible)
  }

  function _areSomeGroupsVisible(layers: DrillLayer[], labels: DrillLabelLayer[]): boolean {
    if (!layers.some(l => l.type === 'cluster')) return labels.some(l => l.visible)

    const cLabels = layers.flatMap(x => x.files.flatMap(y => y.labels))

    return layers.some(l => l.visible) || cLabels.some(l => l.visible)
  }

  function _filterHolesBySearchTerm(drillLayers: DrillLayer[], searchTerm: string): DrillLayer[] {
    const searchFilters = searchTerm.split(' ').map(makeEscapedRegExp)

    const holes = drillLayers.map(hole => ({
      ...hole,
      files: hole.files.map(file => ({
        ...file,
        labels: file.labels.filter(label => searchFilters.every(x => x.test(label.title))),
      })),
    }))

    if (!holes.flatMap(h => h.files).length) {
      return holes.filter(h => searchFilters.every(x => x.test(h.title)))
    }

    if (!holes.flatMap(h => h.files.flatMap(f => f.labels)).length) {
      return []
    }

    return holes
  }

  return {
    layerGroups: shallowReadonly(layerGroups),
    clearLayerGroups,
    rebuildLayerGroups,
  }
}
